"use client"

import { useState } from "react"
import Image from "next/image"
import { cn } from "@/lib/utils"
import {OverlayIcon} from "@/components/common/overlay-icon";

const SETTINGS_SECTIONS = [
  {
    id: "defaults",
    label: "Defaults",
    icon: "/ic_settings.svg",
  },
] as const

type SectionId = (typeof SETTINGS_SECTIONS)[number]["id"]

/** Sidebar nav list (no page title — title sits above this card). */
export function ProjectSettingsNav({
                                     activeSection = "defaults",
                                     onSectionChange,
                                   }: {
  activeSection?: SectionId
  onSectionChange?: (id: SectionId) => void
}) {
  return (
      <div className="flex h-full min-h-0 flex-col px-2.5 pt-2.5 pb-3">
        {SETTINGS_SECTIONS.map((section) => {
          const isActive = activeSection === section.id
          return (
              <button
                  key={section.id}
                  type="button"
                  onClick={() => onSectionChange?.(section.id)}
                  style={
                    isActive
                        ? {
                          borderLeftColor: "var(--ed-user-header)",
                          color: "var(--ed-user-header)",
                        }
                        : undefined
                  }
                  className={cn(
                      "flex w-full items-center gap-2.5 overflow-hidden py-[11px] text-left text-[13px] font-medium transition-colors",
                      isActive
                          ? "rounded-lg border-l-[4px] bg-[var(--ed-settings-active-bg,#E0E9FF)] dark:bg-white/10 pl-[10px]"
                          : "rounded-lg pl-[14px] text-muted-foreground hover:bg-black/5 dark:hover:bg-white/5 hover:text-foreground",
                  )}
              >
                <OverlayIcon
                    src={section.icon}
                    alt=""
                    width={16}
                    height={16}
                    // className={cn(
                    //     "shrink-0",
                    //     isActive
                    //         ? "[filter:var(--ed-icon-filter-primary,invert(48%)_sepia(79%)_saturate(1200%)_hue-rotate(196deg)_brightness(101%)_contrast(101%))]"
                    //         : "opacity-60 dark:opacity-80 dark:invert",
                    // )}
                />
                {section.label}
              </button>
          )
        })}
      </div>
  )
}

function ModeToggle({
                      checked,
                      onCheckedChange,
                    }: {
  checked: boolean
  onCheckedChange: (checked: boolean) => void
}) {
  return (
      <button
          type="button"
          role="switch"
          aria-checked={checked}
          aria-label={checked ? "Read only Mode" : "Edit Mode"}
          onClick={() => onCheckedChange(!checked)}
          style={checked ? { backgroundColor: "var(--ed-user-header)" } : undefined}
          className={cn(
              "relative h-[26px] w-[48px] shrink-0 rounded-full transition-colors duration-200 focus:outline-none",
              !checked && "bg-[#D1D5DB] dark:bg-neutral-700",
          )}
      >
      <span
          className={cn(
              "absolute top-[3px] left-[3px] size-5 rounded-full bg-white dark:bg-neutral-200 shadow-[0_1px_3px_rgba(0,0,0,0.25)] transition-transform duration-200",
              checked ? "translate-x-[22px]" : "translate-x-0",
          )}
      />
      </button>
  )
}

/** Main settings form shown in the dock area. */
export function ProjectSettingsContent() {
  const [readOnlyMode, setReadOnlyMode] = useState(false)
  const [draftReadOnly, setDraftReadOnly] = useState(false)

  const handleUpdate = () => {
    setReadOnlyMode(draftReadOnly)
  }

  const handleCancel = () => {
    setDraftReadOnly(readOnlyMode)
  }

  return (
      <div className="flex h-full min-h-0 flex-1 flex-col bg-[var(--ed-explorer-canvas-bg)] p-2">
        {/* Matches sidebar "Defaults" title row so both cards share the same top edge */}
        {/*<div className="h-[27px] shrink-0" aria-hidden />*/}

        <div
            className={cn(
                "flex min-h-0 flex-1 flex-col overflow-y-auto rounded-2xl border border-[var(--ed-explorer-panel-border)] bg-white dark:bg-[var(--ed-panel-bg,#262626)] shadow-sm",
            )}
        >
          <div className="px-8 pt-7 pb-0">
            <h1 className="text-[18px] font-bold leading-tight tracking-tight text-foreground">
              Dashboard Defaults
            </h1>
            <p className="mt-1 text-[13px] leading-snug text-muted-foreground">
              Document defaults
            </p>
            <div className="mt-4 border-b border-border" />
          </div>

          <div className="px-8 pt-5 pb-8">
            <h2
                className="mb-4 text-[15px] font-semibold"
                style={{ color: "var(--ed-user-header)" }}
            >
              Defaults
            </h2>

            <div className="mb-8 flex flex-wrap items-center gap-2.5">
            <span className="text-[13px] font-medium text-foreground">
              Edit Mode
            </span>
              <ModeToggle
                  checked={draftReadOnly}
                  onCheckedChange={setDraftReadOnly}
              />
              <span className="text-[13px] font-medium text-foreground">
              Read only Mode
            </span>
            </div>

            <div className="flex gap-2.5">
                <button
                    type="button"
                    onClick={handleUpdate}
                    style={{ backgroundColor: "var(--ed-user-header, #5B8CFF)" }}
                    className="h-9 rounded-md px-6 text-[13px] font-medium text-white dark:text-black transition-opacity hover:opacity-90"
                >
                    Update
                </button>
              <button
                  type="button"
                  onClick={handleCancel}
                  style={{ borderColor: "var(--ed-explorer-panel-border, #C7CDDB)" }}
                  className="h-9 rounded-md border bg-transparent px-6 text-[13px] font-medium text-foreground transition-colors hover:bg-black/5 dark:hover:bg-white/5"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
  )
}
